/**
 * Custom hook for managing topics data
 */

import { useState, useEffect } from 'react';
import { getTopics } from '../services/topicService';
import type { Topic } from '@/types/topic';

interface UseTopicsReturn {
  topics: Topic[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Custom hook to fetch and manage topics with UI enhancements
 * @returns Object containing topics data, loading state, error state, and refetch function
 */
export const useTopics = (): UseTopicsReturn => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTopics = async () => {
    try {
      console.log('🔄 [useTopics] Starting to fetch topics...');
      setLoading(true);
      setError(null);

      const response = await getTopics();
      console.log('📥 [useTopics] Response received from service:', {
        success: response.success,
        dataCount: response.data.length,
        message: response.message
      });

      if (response.success) {
        console.log('✅ [useTopics] Topics successfully processed:', response.data);
        setTopics(response.data);
      } else {
        console.log('❌ [useTopics] API returned error:', response.message);
        setError(response.message || 'Failed to load topics');
      }
    } catch (err) {
      console.error('🚨 [useTopics] Unexpected error:', err);
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      setError(errorMessage);
    } finally {
      console.log('🏁 [useTopics] Fetch process completed, loading state set to false');
      setLoading(false);
    }
  };

  const refetch = async () => {
    await fetchTopics();
  };

  useEffect(() => {
    fetchTopics();
  }, []);

  return {
    topics,
    loading,
    error,
    refetch
  };
};
